import { apiClient } from './api';

// ─── Attendance ────────────────────────────────────────────────────────────
export interface AttendanceEvent {
  id: string;
  type: 'START' | 'BREAK_OUT' | 'BREAK_IN' | 'END';
  occurredAt: string;
  isManual: boolean;
  reason?: string;
}
export interface AttendanceSession {
  id: string;
  workDate: string;
  status: 'IN_PROGRESS' | 'ON_BREAK' | 'COMPLETED' | 'NEEDS_REVIEW';
  startedAt: string;
  endedAt?: string;
  actualWorkMinutes: number;
  actualBreakMinutes: number;
  billableWorkMinutes: number;
  billableBreakMinutes: number;
  needsReview: boolean;
  events?: AttendanceEvent[];
  staff?: { id: string; firstName: string; lastName: string; salonId: string };
  salon?: { id: string; name: string };
}
export interface MyStatus {
  staff: { id: string; firstName: string; lastName: string; salonId: string };
  status: 'OFF' | 'IN_PROGRESS' | 'ON_BREAK' | 'COMPLETED';
  openSession: AttendanceSession | null;
  todaySessions: AttendanceSession[];
  startedAt: string | null;
  totalActualMinutes: number;
  totalBillableMinutes: number;
}

export const attendanceApi = {
  myStatus: () => apiClient.get<MyStatus>('/attendance/me/status').then((r) => r.data),
  clockIn: () => apiClient.post<AttendanceSession>('/attendance/me/clock-in').then((r) => r.data),
  breakOut: () => apiClient.post<AttendanceSession>('/attendance/me/break-out').then((r) => r.data),
  breakIn: () => apiClient.post<AttendanceSession>('/attendance/me/break-in').then((r) => r.data),
  clockOut: () => apiClient.post<AttendanceSession>('/attendance/me/clock-out').then((r) => r.data),
  requestAdjustment: (reason: string, sessionId?: string) =>
    apiClient.post('/attendance/me/adjustment', { reason, sessionId }).then((r) => r.data),

  listSessions: (params: Record<string, unknown>) =>
    apiClient.get<AttendanceSession[]>('/attendance/sessions', { params }).then((r) => r.data),
  createSession: (body: { staffId: string; workDate: string; startedAt: string; endedAt: string; reason: string }) =>
    apiClient.post<AttendanceSession>('/attendance/sessions', body).then((r) => r.data),
  today: (salonId: string) =>
    apiClient.get<AttendanceSession[]>(`/attendance/today/${salonId}`).then((r) => r.data),
  flagStale: (salonId: string) => apiClient.post(`/attendance/flag-stale/${salonId}`).then((r) => r.data),
  adjustSession: (id: string, body: Record<string, unknown>) =>
    apiClient.patch<AttendanceSession>(`/attendance/sessions/${id}`, body).then((r) => r.data),
  listAdjustments: (params: Record<string, unknown>) =>
    apiClient.get('/attendance/adjustments', { params }).then((r) => r.data),
  reviewAdjustment: (id: string, body: Record<string, unknown>) =>
    apiClient.patch(`/attendance/adjustments/${id}`, body).then((r) => r.data),
  getSettings: (salonId: string) => apiClient.get(`/attendance/settings/${salonId}`).then((r) => r.data),
  updateSettings: (salonId: string, body: Record<string, unknown>) =>
    apiClient.put(`/attendance/settings/${salonId}`, body).then((r) => r.data),
};

// ─── Absences ────────────────────────────────────────────────────────────
export interface Absence {
  id: string;
  type: 'SICK_LEAVE' | 'VACATION' | 'EXCUSED' | 'UNEXCUSED' | 'OTHER';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'NEEDS_INFO';
  startDate: string;
  endDate: string;
  note?: string;
  staff?: { id: string; firstName: string; lastName: string };
}
export const absenceApi = {
  create: (body: Record<string, unknown>) => apiClient.post<Absence>('/absences', body).then((r) => r.data),
  mine: () => apiClient.get<Absence[]>('/absences/me').then((r) => r.data),
  list: (params: Record<string, unknown>) => apiClient.get<Absence[]>('/absences', { params }).then((r) => r.data),
  // Nieobecnosci nakladajace sie na zadany zakres — do kalendarza salonu.
  // W odroznieniu od list() zwraca takze te rozpoczete przed `from`.
  calendar: (params: { salonId?: string; staffId?: string; from?: string; to?: string }) =>
    apiClient.get<Absence[]>('/absences/calendar', { params }).then((r) => r.data),
  review: (id: string, status: string, reviewNote?: string) =>
    apiClient.patch(`/absences/${id}`, { status, reviewNote }).then((r) => r.data),
};

// ─── Employees ────────────────────────────────────────────────────────────
export interface TeamMember {
  userId: string;
  staffId: string | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: 'SUPER_ADMIN' | 'SALON_OWNER' | 'MANAGER' | 'STAFF' | 'ACCOUNTANT' | 'ANALYST' | 'MARKETER' | 'IT' | 'CLIENT';
  isActive: boolean;
  isOwner: boolean;
  isManagerHere: boolean;
}

export interface InviteTeamMemberInput {
  salonId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
  expiresAt: string;
  expired: boolean;
}

export const employeeApi = {
  list: (salonId?: string) => apiClient.get('/employees', { params: salonId ? { salonId } : {} }).then((r) => r.data),
  detail: (staffId: string) => apiClient.get(`/employees/${staffId}`).then((r) => r.data),
  data: (staffId: string, year: number, month: number) =>
    apiClient.get(`/employees/${staffId}/data`, { params: { year, month } }).then((r) => r.data),
  upsertProfile: (staffId: string, body: Record<string, unknown>) =>
    apiClient.put(`/employees/${staffId}/profile`, body).then((r) => r.data),
  setLocations: (staffId: string, salonIds: string[], primarySalonId?: string) =>
    apiClient.put(`/employees/${staffId}/locations`, { salonIds, primarySalonId }).then((r) => r.data),
  addSale: (staffId: string, body: Record<string, unknown>) =>
    apiClient.post(`/employees/${staffId}/sales`, body).then((r) => r.data),
  importCsv: (csv: string) => apiClient.post('/employees/import-sales', { csv }).then((r) => r.data),

  // Team & roles
  team: (salonId: string) => apiClient.get<TeamMember[]>(`/employees/team/${salonId}`).then((r) => r.data),
  invite: (input: InviteTeamMemberInput) => apiClient.post('/employees/team', input).then((r) => r.data),
  invitations: (salonId: string) =>
    apiClient.get<PendingInvitation[]>(`/employees/team/${salonId}/invitations`).then((r) => r.data),
  resendInvitation: (id: string) => apiClient.post(`/employees/invitations/${id}/resend`).then((r) => r.data),
  cancelInvitation: (id: string) => apiClient.delete(`/employees/invitations/${id}`).then((r) => r.data),
  setRole: (userId: string, salonId: string, role: string) =>
    apiClient.patch(`/employees/team/${userId}/role`, { salonId, role }).then((r) => r.data),
  setPassword: (userId: string, salonId: string, password: string) =>
    apiClient.post(`/employees/team/${userId}/password`, { salonId, password }).then((r) => r.data),
  assignManager: (userId: string, salonId: string) =>
    apiClient.post('/employees/managers', { userId, salonId }).then((r) => r.data),
  removeManager: (userId: string, salonId: string) =>
    apiClient.delete('/employees/managers', { data: { userId, salonId } }).then((r) => r.data),
};

// ─── Payroll ────────────────────────────────────────────────────────────
export interface PayrollItem {
  id: string;
  staffId: string;
  actualWorkMinutes: number;
  billableWorkMinutes: number;
  clientsCount: number;
  servicesRevenue: number;
  productsRevenue: number;
  materialsCost: number;
  components: { code: string; label: string; amount: number }[];
  grossSalary: number;
  employeeZus: number;
  netPaid: number;
  employerZus: number;
  employerCost: number;
  generatedRevenue: number;
  salonResult: number;
  profitabilityPct: number;
  isCorrection: boolean;
  staff?: { id: string; firstName: string; lastName: string };
}
export interface PayrollPeriod {
  id: string;
  salonId: string;
  year: number;
  month: number;
  status: 'DRAFT' | 'REVIEW' | 'APPROVED' | 'PAID' | 'CANCELLED';
  approvedAt?: string;
  paidAt?: string;
  salon?: { id: string; name: string };
  items?: PayrollItem[];
  _count?: { items: number };
}
export const payrollApi = {
  listPeriods: (salonId?: string) =>
    apiClient.get<PayrollPeriod[]>('/payroll/periods', { params: salonId ? { salonId } : {} }).then((r) => r.data),
  ensurePeriod: (salonId: string, year: number, month: number) =>
    apiClient.post<PayrollPeriod>('/payroll/periods', { salonId, year, month }).then((r) => r.data),
  getPeriod: (id: string) => apiClient.get<PayrollPeriod>(`/payroll/periods/${id}`).then((r) => r.data),
  generate: (id: string) => apiClient.post<PayrollPeriod>(`/payroll/periods/${id}/generate`).then((r) => r.data),
  transition: (id: string, status: string) =>
    apiClient.patch(`/payroll/periods/${id}/status`, { status }).then((r) => r.data),
  correction: (id: string, staffId: string) =>
    apiClient.post(`/payroll/periods/${id}/correction`, { staffId }).then((r) => r.data),
  // Downloads the CSV via an authenticated request (a plain <a href> would omit
  // the JWT and get 401), then triggers a browser download from the blob.
  exportCsv: async (id: string, filename = `rozliczenia-${id}.csv`) => {
    const res = await apiClient.get(`/payroll/periods/${id}/export`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  history: (staffId: string) => apiClient.get<PayrollItem[]>(`/payroll/history/${staffId}`).then((r) => r.data),
  listRules: (staffId: string) => apiClient.get(`/payroll/rules/${staffId}`).then((r) => r.data),
  upsertRule: (staffId: string, body: Record<string, unknown>) =>
    apiClient.post(`/payroll/rules/${staffId}`, body).then((r) => r.data),
};

// ─── Profitability ────────────────────────────────────────────────────────
export const profitabilityApi = {
  staffMonth: (staffId: string, year: number, month: number) =>
    apiClient.get(`/profitability/staff/${staffId}`, { params: { year, month } }).then((r) => r.data),
  staffYear: (staffId: string, year: number) =>
    apiClient.get(`/profitability/staff/${staffId}/year`, { params: { year } }).then((r) => r.data),
  salonMonth: (salonId: string, year: number, month: number) =>
    apiClient.get(`/profitability/salon/${salonId}`, { params: { year, month } }).then((r) => r.data),
};

// ─── Simulations ────────────────────────────────────────────────────────────
export const simulationApi = {
  run: (config: unknown, inputs: unknown) =>
    apiClient.post('/simulations/run', { config, inputs }).then((r) => r.data),
  save: (body: Record<string, unknown>) => apiClient.post('/simulations', body).then((r) => r.data),
  list: (staffId?: string) =>
    apiClient.get('/simulations', { params: staffId ? { staffId } : {} }).then((r) => r.data),
  remove: (id: string) => apiClient.delete(`/simulations/${id}`).then((r) => r.data),
};

// ─── Notifications ────────────────────────────────────────────────────────
export const notificationApi = {
  list: (unread = false) => apiClient.get('/notifications', { params: { unread } }).then((r) => r.data),
  unreadCount: () => apiClient.get<{ count: number }>('/notifications/unread-count').then((r) => r.data),
  markRead: (id: string) => apiClient.patch(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => apiClient.patch('/notifications/read-all').then((r) => r.data),
  auditLogs: (params: Record<string, unknown>) =>
    apiClient.get('/notifications/audit-logs', { params }).then((r) => r.data),
};

export function fmtMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
}
